import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PaymentIcon from '@mui/icons-material/Payment';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const EcommerceNotificationPanel = ({ open, onClose, onOrderConfirmed }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/ecommerce-store/me/pending-orders');
      if (res.data?.success) {
        setOrders(res.data.data || []);
      }
    } catch (e) {
      console.error('Failed to load ecommerce orders:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadOrders();
    }
  }, [open]);

  const handleConfirm = async (orderId) => {
    setConfirming(orderId);
    try {
      const res = await axios.post(`/api/ecommerce-store/me/confirm-order/${orderId}`);
      if (res.data?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Order Confirmed!',
          text: 'Order has been added to ongoing orders',
          timer: 2000,
          showConfirmButton: false
        });
        
        // Remove confirmed order from list immediately
        setOrders(prevOrders => prevOrders.filter(o => o._id !== orderId));
        
        // Also refresh to ensure sync
        setTimeout(() => loadOrders(), 500);
      } else {
        Swal.fire('Error', res.data?.message || 'Failed to confirm order', 'error');
      }
    } catch (e) {
      Swal.fire('Error', e.response?.data?.message || 'Failed to confirm order', 'error');
    } finally {
      setConfirming(null);
    }
  };

  const handlePayment = async (orderId) => {
    console.log('🔵 [ECOMMERCE] Confirm & Pay clicked for order:', orderId);
    setConfirming(orderId);
    
    try {
      console.log('🔵 [ECOMMERCE] Calling confirm-order API...');
      const res = await axios.post(`/api/ecommerce-store/me/confirm-order/${orderId}`);
      console.log('🔵 [ECOMMERCE] API response:', res.data);
      
      if (res.data?.success) {
        const posOrderId = res.data.data?.posOrderId;
        console.log('🔵 [ECOMMERCE] POS Order ID:', posOrderId);
        
        if (posOrderId) {
          // Remove order from notification list immediately
          setOrders(prevOrders => prevOrders.filter(o => o._id !== orderId));
          
          // Close dialog first
          onClose();
          
          // Navigate using URL parameter (forces proper page navigation)
          const targetUrl = `/userdashboard/billing?orderId=${posOrderId}`;
          console.log('🔵 [ECOMMERCE] Navigating to:', targetUrl);
          window.location.href = targetUrl;
        } else {
          console.error('❌ [ECOMMERCE] No POS order ID in response');
          Swal.fire('Error', 'POS order was not created', 'error');
        }
      } else {
        console.error('❌ [ECOMMERCE] API returned success=false:', res.data?.message);
        Swal.fire('Error', res.data?.message || 'Failed to confirm order', 'error');
      }
    } catch (e) {
      console.error('❌ [ECOMMERCE] API error:', e.response?.data || e.message);
      Swal.fire('Error', e.response?.data?.message || 'Failed to confirm order', 'error');
    } finally {
      setConfirming(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING_COUNTER_CONFIRM':
        return 'warning';
      case 'PENDING_PAYMENT':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDING_COUNTER_CONFIRM':
        return 'Awaiting Confirmation';
      case 'PENDING_PAYMENT':
        return 'Payment Pending';
      default:
        return status;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>
            Ecommerce Orders
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={loadOrders} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Alert severity="info">No pending orders</Alert>
        ) : (
          <List>
            {orders.map((order, idx) => (
              <React.Fragment key={order._id}>
                {idx > 0 && <Divider />}
                <ListItem
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    py: 2
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, width: '100%' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Order #{String(order._id || '').slice(-8).toUpperCase()}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {order.customer?.name || 'Customer'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        📱 {order.customer?.phone || 'No phone'}
                      </Typography>
                      {order.customer?.address && (
                        <Typography variant="caption" color="text.secondary">
                          📍 {order.customer.address}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={getStatusLabel(order.status)}
                      color={getStatusColor(order.status)}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                      Items:
                    </Typography>
                    {(order.items || []).map((item, idx) => (
                      <Typography key={idx} variant="body2" color="text.secondary">
                        • {item.name || 'Item'} × {item.qty || 0} = {(item.lineTotal || 0).toFixed(2)}
                      </Typography>
                    ))}
                    <Typography variant="body2" fontWeight={600} mt={0.5}>
                      Total: {order.total?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Payment: {order.paymentMode === 'ONLINE' ? '💳 Online' : '💵 Pay at Counter'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    {order.status === 'PENDING_COUNTER_CONFIRM' && (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={() => handleConfirm(order._id)}
                        disabled={confirming === order._id}
                      >
                        {confirming === order._id ? 'Confirming...' : 'Confirm'}
                      </Button>
                    )}
                    {order.status === 'PENDING_PAYMENT' && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<PaymentIcon />}
                        onClick={() => handlePayment(order._id)}
                        disabled={confirming === order._id}
                      >
                        {confirming === order._id ? 'Processing...' : 'Process Payment'}
                      </Button>
                    )}
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EcommerceNotificationPanel;
